import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";

import Typography from "@mui/material/Typography";

import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Stack from "@mui/material/Stack";
import Link from "@mui/material/Link";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";

import Search from "components/forms/Search";
import Pagination from "components/elements/Pagination";

import { ThemeContext } from "components/common/App";

import rpc, { buildRequest, signoutAndRedirectToIndex } from "lib/rpc";

export default function MaterialsPage() {
  const theme = useContext(ThemeContext);

  let { page = 1 } = useParams();
  const navigate = useNavigate();

  const [materials, setMaterials] = useState();
  const [searchString, setSearchString] = useState("");
  const [perPage, setPerPage] = useState(15);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    theme.changeTheme("light-bg");
    window.scrollTo(0, 0); // Make sure to be on top of the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMaterials(null);
    rpc
      .post(
        "",
        buildRequest("admin.materials.list", {
          page: page,
          per_page: perPage,
          search: searchString,
        }),
      )
      .then((response) => {
        if (!response.data.result.message) {
          setMaterials(response.data.result.data);
          setTotal(response.data.result.records);
        } else {
          if (response.data.result.code === 5) {
            signoutAndRedirectToIndex();
          }
          console.error(response.data.result);
        }
      });
  }, [page, perPage, searchString]);

  const setPage = (newPage) => {
    navigate(`/materials/${newPage}`, { replace: true });
  };

  return (
    <>
      <Helmet>
        <title>Materials</title>
      </Helmet>

      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
        component={RouterLink}
        to="/materials/create"
      >
        <AddIcon />
      </Fab>

      <Container fixed sx={{ paddingBottom: 7 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h3" gutterBottom>
              Materials
            </Typography>
          </Stack>

          <Paper>
            {!materials && <CircularProgress />}
            <Search value={searchString} setSearchString={setSearchString} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell align="left">Name</TableCell>
                    <TableCell align="left">File</TableCell>
                    <TableCell align="left">Created</TableCell>
                    <TableCell align="left">Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {materials &&
                    materials.map((material) => (
                      <TableRow key={`material-${material.id}`}>
                        <TableCell>
                          <Link to={`/materials/details/${material.id}`} component={RouterLink}>
                            {material.id}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link to={`/materials/details/${material.id}`} component={RouterLink}>
                            {material.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <a href={`${material.download_link}`} target="_blank" rel="noopener noreferrer">
                            Download file
                          </a>
                        </TableCell>
                        <TableCell>{material.created_on}</TableCell>
                        <TableCell>{material.updated_on}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
                <Pagination page={page} total={total} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Container>
    </>
  );
}
